import type { z } from "zod";

import type { AccountOpener } from "../../pages/dashboard/entityOnboarding/pages/accountOpener/schema";
import type {
  AuthorizedIndividual,
  UltimateBeneficialOwner,
} from "../../pages/dashboard/entityOnboarding/pages/authIndividualsUBOs/schema";
import type { BusinessInfo } from "../../pages/dashboard/entityOnboarding/pages/businessInfo/schema";
import {
  type Transform,
  transformObject,
} from "../../pages/dashboard/entityOnboarding/util";
import instance from "..";
import { looseArray, looseParse } from "../looseParse";
import type { Owner } from "./account";
import { ownerSchema } from "./account.schema";
import { postAgreement } from "./agreements";
import { ownerDocumentSchema } from "./entityOnboarding.schema";

export const toEntityAccount = async (accountId: string): Promise<void> => {
  const res = await instance.patch(`/accounts/${accountId}/to-entity-account`);
  return res.data;
};

export const toIndividualAccount = async (accountId: string): Promise<void> => {
  const res = await instance.patch(
    `/accounts/${accountId}/to-individual-account`,
  );
  return res.data;
};

export type PostOwnerArgs =
  | { type: "legal-entity"; accountId: string; data: BusinessInfo }
  | { type: "account-opener"; accountId: string; data: AccountOpener }
  | {
      type: "authorized-individual";
      accountId: string;
      data: AuthorizedIndividual;
    }
  | { type: "ubo"; accountId: string; data: UltimateBeneficialOwner };

export type OwnerType = PostOwnerArgs["type"];

export type PatchOwnerArgs =
  | {
      type: "legal-entity";
      accountId: string;
      ownerId: string;
      data: Partial<BusinessInfo>;
    }
  | {
      type: "account-opener";
      accountId: string;
      ownerId: string;
      data: Partial<AccountOpener>;
    }
  | {
      type: "authorized-individual";
      accountId: string;
      ownerId: string;
      data: Partial<AuthorizedIndividual>;
    }
  | {
      type: "ubo";
      accountId: string;
      ownerId: string;
      data: Partial<UltimateBeneficialOwner>;
    };

type PersonOwnerData =
  | AccountOpener
  | AuthorizedIndividual
  | UltimateBeneficialOwner;

const legalEntityTransforms: [
  Transform<BusinessInfo, string, string[]>,
  Transform<BusinessInfo, string, undefined>,
] = [
  {
    keys: ["funding_source"],
    condition: (value) => typeof value === "string",
    transform: (value) => value.split(","),
  },
  {
    keys: "ALL_FIELDS",
    condition: (value) => value === "",
    transform: (_emptyString) => undefined,
  },
];

const personOwnerTransforms: [
  Transform<PersonOwnerData, string, string[]>,
  Transform<PersonOwnerData, string, undefined>,
] = [
  {
    keys: ["controlling_firms"],
    condition: (value) => typeof value === "string",
    transform: (value) => value.split(","),
  },
  {
    keys: "ALL_FIELDS",
    condition: (value) => value === "",
    transform: (_emptyString) => undefined,
  },
];

export const postOwner = async ({ type, accountId, data }: PostOwnerArgs) => {
  const body =
    type === "legal-entity"
      ? transformObject<BusinessInfo, Record<string, unknown>>(
          data,
          legalEntityTransforms as Transform<BusinessInfo>[],
        )
      : transformObject<PersonOwnerData, Record<string, unknown>>(
          data,
          personOwnerTransforms as Transform<PersonOwnerData>[],
        );

  const res = await instance.post<Owner>(`/accounts/${accountId}/owners`, body);
  return looseParse(ownerSchema, res.data, "postOwner");
};

export const patchOwner = async ({
  type,
  accountId,
  ownerId,
  data,
}: PatchOwnerArgs) => {
  const body =
    type === "legal-entity"
      ? transformObject<Partial<BusinessInfo>, Record<string, unknown>>(
          data,
          legalEntityTransforms as Transform<Partial<BusinessInfo>>[],
        )
      : transformObject<Partial<PersonOwnerData>, Record<string, unknown>>(
          data as Partial<PersonOwnerData>,
          personOwnerTransforms as Transform<Partial<PersonOwnerData>>[],
        );

  const res = await instance.patch<Owner>(
    `/accounts/${accountId}/owners/${ownerId}`,
    body,
  );
  return looseParse(ownerSchema, res.data, "patchOwner");
};

export const deleteOwner = async ({
  accountId,
  ownerId,
}: {
  accountId: string;
  ownerId: string;
}) => {
  const res = await instance.delete(`/accounts/${accountId}/owners/${ownerId}`);
  return res.data;
};

export type OwnerDocument = z.infer<typeof ownerDocumentSchema>;

export const getOwnerDocuments = async (accountId: string, ownerId: string) => {
  const res = await instance.get<{ documents: OwnerDocument[] }>(
    `/accounts/${accountId}/owners/${ownerId}/documents`,
  );
  return looseParse(
    looseArray(ownerDocumentSchema, "getOwnerDocuments"),
    res.data?.documents,
    "getOwnerDocuments",
  );
};

export const postFileUpload = async ({
  accountId,
  ownerId,
  docType,
  docSubType,
  file,
}: {
  accountId: string;
  ownerId: string;
  docType: string;
  docSubType?: string;
  file: File;
}) => {
  const form = new FormData();
  form.append("uploadfile", file);
  const res = await instance.post<{ id: string; object_key: string }>(
    `/accounts/${accountId}/owners/${ownerId}/documents/${docType}${
      docSubType ? `/${docSubType}` : ""
    }`,
    form,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return res.data;
};

export const deleteFileUpload = async ({
  accountId,
  ownerId,
  documentId,
}: {
  accountId: string;
  ownerId: string;
  documentId: string;
}) => {
  const res = await instance.delete(
    `/accounts/${accountId}/owners/${ownerId}/documents/${documentId}`,
  );
  return res.data;
};

export const getTaxFileForm = async ({
  accountId,
  ownerId,
  form,
}: {
  accountId: string;
  ownerId: string;
  form: "certifications_and_resolutions_statement" | "w_8ben";
}): Promise<Blob> => {
  const res = await instance.get(
    `/accounts/${accountId}/owners/${ownerId}/taxfiles/${form}/preview`,
    { responseType: "blob" },
  );
  return res.data;
};

export const acceptCertificationsForPrimaryAndSigner = async ({
  accountId,
  legalEntityOwnerId,
  signerPartyId,
}: {
  accountId: string;
  legalEntityOwnerId: string;
  signerPartyId: string;
}) => {
  const primaryAcceptance = acceptTaxFileForm({
    accountId,
    ownerId: legalEntityOwnerId,
    form: "certifications_and_resolutions_statement",
    signerPartyId,
  });

  const authorizedSignerAcceptance = acceptTaxFileForm({
    accountId,
    ownerId: signerPartyId,
    form: "certifications_and_resolutions_statement",
  });

  return Promise.all([primaryAcceptance, authorizedSignerAcceptance]);
};

export const acceptTaxFileForm = async ({
  accountId,
  ownerId,
  form,
  signerPartyId,
}: {
  accountId: string;
  ownerId: string;
  form: "certifications_and_resolutions_statement" | "w_8ben";
  signerPartyId?: string;
}) => {
  const body =
    form === "certifications_and_resolutions_statement" && signerPartyId
      ? { signer_party_id: signerPartyId }
      : {};

  const res = await instance.post(
    `/accounts/${accountId}/owners/${ownerId}/taxfiles/${form}/accept`,
    body,
  );

  return res.data;
};

export const submitEntityAccount = async (accountId: string) => {
  const res = await instance.post(`/accounts/${accountId}/submit`);
  return res.data;
};

// Some agreements require both the legal entity and the authorized signer to accept the agreement
export const postAgreementForLegalEntityAndSigner = async (
  params: Parameters<typeof postAgreement>[0],
) => {
  const { ownerId, signerPartyId, ...rest } = params;
  if (!signerPartyId) {
    throw new Error(
      "A signerPartyId is required to post an agreement for both an owner and a signer.",
    );
  }

  const legalEntityCall = postAgreement({
    ownerId,
    signerPartyId,
    ...rest,
  });

  const authorizedSignerCall = postAgreement({
    ownerId: signerPartyId,
    signerPartyId: undefined,
    ...rest,
  });

  return Promise.all([legalEntityCall, authorizedSignerCall]);
};

export const patchOwnerType = async ({
  accountId,
  ownerId,
  isUBO,
  isAuthorizedIndividual,
}: {
  accountId: string;
  ownerId: string;
  isUBO: boolean;
  isAuthorizedIndividual: boolean;
}) => {
  const res = await instance.patch(
    `/accounts/${accountId}/owners/${ownerId}/owner_type`,
    {
      is_ubo: isUBO,
      is_authorized_individual: isAuthorizedIndividual,
      owner_id: ownerId,
    },
  );
  return res.data;
};
